import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { products, Product } from '@/mocks/products';
import ProductQuickView from '@/components/feature/ProductQuickView';

const categories = ['All', 'Makhana', 'Snacks', 'Spices', 'Tea', 'Pickles', 'Water'];

export default function FeaturedProductsSection() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const navigate = useNavigate();

  const filtered = activeCategory === 'All'
    ? products
    : products.filter((p) => p.category === activeCategory);

  return (
    <>
      <section
        className="py-20 md:py-24"
        style={{ background: 'linear-gradient(160deg, #fdf9f4 0%, #f7ede0 50%, #fdf9f4 100%)' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:px-24">

          {/* ── Section Header ── */}
          <div className="text-center mb-4">
            <p
              className="text-xs font-bold uppercase tracking-[0.25em] mb-3"
              style={{ color: '#c9a96e' }}
            >
              Handcrafted with Love
            </p>
            <h2
              className="font-bold leading-tight mb-3"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
                color: '#2c1a0e',
              }}
            >
              Our Featured Products
            </h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: '#7a4f2a' }}>
              Premium quality, authentic taste — 9 handcrafted products across 5 categories, made with care and integrity.
            </p>
          </div>

          {/* Decorative divider */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="h-px w-16" style={{ background: '#e8d5b7' }}></div>
            <i className="ri-flower-line text-sm" style={{ color: '#c9a96e' }}></i>
            <div className="h-px w-16" style={{ background: '#e8d5b7' }}></div>
          </div>

          {/* ── Category Filter Pills ── */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="px-5 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={
                  activeCategory === cat
                    ? { background: '#2c1a0e', color: '#fdf9f4', border: '1.5px solid #2c1a0e' }
                    : { background: 'transparent', color: '#7a4f2a', border: '1.5px solid #d4b896' }
                }
              >
                {cat}
              </button>
            ))}
          </div>

          {/* ── Products Grid ── */}
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 md:gap-6"
            data-product-shop
          >
            {filtered.map((product) => (
              <div
                key={product.id}
                className="group flex flex-col rounded-2xl overflow-hidden cursor-pointer transition-all duration-300"
                style={{
                  background: '#fff',
                  border: hoveredId === product.id ? '1.5px solid #c9a96e' : '1.5px solid #ecdfc8',
                  transform: hoveredId === product.id ? 'translateY(-4px)' : 'translateY(0)',
                }}
                onMouseEnter={() => setHoveredId(product.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => navigate(`/products/${product.id}`)}
              >
                {/* Image area */}
                <div
                  className="relative overflow-hidden"
                  style={{ aspectRatio: '1/1', background: '#fdf6ec' }}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-108"
                    style={{ transform: hoveredId === product.id ? 'scale(1.07)' : 'scale(1)' }}
                  />

                  {/* Badge */}
                  {product.badge && (
                    <span
                      className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                      style={{ background: '#c9a96e', color: '#fff' }}
                    >
                      {product.badge}
                    </span>
                  )}

                  {/* Weight pill */}
                  {product.weight && (
                    <span
                      className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.9)', color: '#7a4f2a', border: '1px solid #e8d5b7' }}
                    >
                      {product.weight}
                    </span>
                  )}

                  {/* Quick view hover overlay */}
                  <div
                    className="absolute inset-0 flex items-end justify-center pb-4 transition-all duration-300"
                    style={{
                      background: 'linear-gradient(to top, rgba(44,26,14,0.55) 0%, transparent 60%)',
                      opacity: hoveredId === product.id ? 1 : 0,
                    }}
                  >
                    <span
                      className="text-xs font-semibold px-5 py-2 rounded-full whitespace-nowrap"
                      style={{ background: '#fdf9f4', color: '#2c1a0e' }}
                    >
                      View Details
                    </span>
                  </div>
                </div>

                {/* Card body */}
                <div className="flex flex-col flex-1 p-4">
                  {/* Brand */}
                  <p
                    className="text-[10px] font-bold uppercase tracking-widest mb-1"
                    style={{ color: '#c9a96e' }}
                  >
                    {product.brand}
                  </p>

                  {/* Name */}
                  <h3
                    className="font-semibold text-sm leading-snug mb-1.5 line-clamp-2 flex-1"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                  >
                    {product.name}
                  </h3>

                  {/* Description */}
                  <p className="text-[11px] leading-relaxed mb-3 line-clamp-2" style={{ color: '#9a7a5a' }}>
                    {product.description}
                  </p>

                  {/* Tags */}
                  {product.tags && product.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {product.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="text-[9px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: '#fdf6ec', color: '#7a4f2a', border: '1px solid #e8d5b7' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Price + CTA */}
                  <div className="flex items-center justify-between mt-auto pt-3" style={{ borderTop: '1px solid #f0e4d0' }}>
                    <div>
                      {product.weight && (
                        <span className="text-xs font-semibold" style={{ color: '#9a7a5a' }}>
                          {product.weight}
                        </span>
                      )}
                    </div>
                    <button
                      className="w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
                      style={{
                        background: hoveredId === product.id ? '#2c1a0e' : '#fdf6ec',
                        color: hoveredId === product.id ? '#fdf9f4' : '#7a4f2a',
                        border: '1.5px solid #d4b896',
                      }}
                      onClick={(e) => { e.stopPropagation(); setQuickViewProduct(product); }}
                    >
                      <i className="ri-eye-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Bottom CTA ── */}
          <div className="text-center mt-14">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-20" style={{ background: '#e8d5b7' }}></div>
              <i className="ri-leaf-line text-sm" style={{ color: '#c9a96e' }}></i>
              <div className="h-px w-20" style={{ background: '#e8d5b7' }}></div>
            </div>
            <p
              className="text-sm mb-5"
              style={{ fontFamily: "'Playfair Display', serif", color: '#7a4f2a', fontStyle: 'italic' }}
            >
              Explore the full range of authentic Indian flavours
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2.5 font-semibold px-10 py-3.5 rounded-md text-sm transition-all whitespace-nowrap"
              style={{ background: '#2c1a0e', color: '#fdf9f4' }}
            >
              Shop All Products
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

        </div>
      </section>

      <ProductQuickView product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />
    </>
  );
}
